/**
 * Marketplace Public Service - Story 7.1: Create Marketplace Homepage
 * API client for public marketplace endpoints (no authentication required)
 * Service: Commerce Service (port 8083)
 */

import type {
  PublicProduct,
  PaginatedPublicProductsResponse,
  FeaturedProductsParams,
} from '../types/publicMarketplace.types';
import type { ProductImage } from '../types/products.types';

// Use relative path in dev mode for Vite proxy
const isDevelopment = import.meta.env.DEV;
const COMMERCE_SERVICE_URL = isDevelopment
  ? '/commerce-service'
  : (import.meta.env.VITE_COMMERCE_SERVICE_URL || 'http://localhost:8083/commerce-service');
const API_BASE = '/api/v1/public/marketplace';

/**
 * Custom error class for marketplace public API errors
 */
export class MarketplacePublicApiError extends Error {
  constructor(
    message: string,
    public readonly statusCode?: number,
    public readonly errorCode?: string
  ) {
    super(message);
    this.name = 'MarketplacePublicApiError';
  }
}

/**
 * Transform API response to PublicProduct format
 * Handles different field names from the backend API
 */
function transformApiProductToPublicProduct(apiProduct: any): PublicProduct {
  // Map category from API to valid ProductCategory type
  const validCategories = ['BIOGAS', 'BIOMASS', 'SEEDS', 'EQUIPMENT', 'FERTILIZER', 'OTHER'];
  const apiCategory = apiProduct.category || 'OTHER';
  const category = validCategories.includes(apiCategory) ? apiCategory : 'OTHER';

  // Map status from API to valid status type
  const validStatuses = ['ACTIVE', 'INACTIVE'];
  const apiStatus = apiProduct.status || 'ACTIVE';
  const status = validStatuses.includes(apiStatus) ? apiStatus : 'ACTIVE';

  // Transform images: backend stores as array of strings, frontend needs array of objects
  const transformedImages: ProductImage[] = [];
  if (apiProduct.images && Array.isArray(apiProduct.images) && apiProduct.images.length > 0) {
    apiProduct.images.forEach((imageUrl: string | any, index: number) => {
      // Handle both string URLs and object formats from backend
      const url = typeof imageUrl === 'string' ? imageUrl : imageUrl.url;
      if (url) {
        // Backend now returns full remote file service URLs (https://saubhagya-dev.dhanushhealthcare.com/file-service/uploads/...)
        // No URL transformation needed - use as-is
        transformedImages.push({
          id: `${index}`,
          url: url,
          altText: apiProduct.productName || `Product image ${index + 1}`,
          isPrimary: index === 0, // First image is primary
        });
      }
    });
  }

  return {
    id: apiProduct.id?.toString() || apiProduct.externalId || '',
    name: apiProduct.productName || apiProduct.name || '',
    description: apiProduct.description || '',
    category: category as any,
    price: apiProduct.price ? parseFloat(apiProduct.price as any) : 0,
    unit: apiProduct.unit || '',
    images: transformedImages,
    location: {
      state: apiProduct.location?.state || apiProduct.state || 'Unknown',
      district: apiProduct.location?.district || apiProduct.district || 'Unknown',
    },
    createdAt: apiProduct.createdAt || new Date().toISOString(),
    status: status as any,
    minimumOrderQuantity: apiProduct.minimumOrderQuantity ? parseInt(apiProduct.minimumOrderQuantity, 10) : undefined,
  };
}

/**
 * Fetch featured products from public marketplace
 * GET /api/v1/public/marketplace/products
 *
 * @param params - Query parameters (page, size, sort)
 * @returns Paginated products response
 * @throws MarketplacePublicApiError on API failure
 *
 * @example
 * const response = await fetchFeaturedProducts({
 *   page: 0,
 *   size: 12,
 *   sort: 'createdAt,desc'
 * });
 */
export async function fetchFeaturedProducts(
  params: FeaturedProductsParams
): Promise<PaginatedPublicProductsResponse> {
  try {
    console.log('[MarketplacePublicService] Fetching featured products with params:', params);

    // Build query string
    const queryParams = new URLSearchParams({
      page: params.page.toString(),
      size: params.size.toString(),
      sort: params.sort,
    });

    const url = `${COMMERCE_SERVICE_URL}${API_BASE}/products?${queryParams.toString()}`;

    const response = await fetch(url, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new MarketplacePublicApiError(
        errorData.message || `HTTP error! status: ${response.status}`,
        response.status,
        errorData.errorCode
      );
    }

    const responseData = await response.json();

    // Handle different response formats
    let data: PaginatedPublicProductsResponse;

    if (responseData.success) {
      // Format: { success: true, data: { data: [], pagination: {} } }
      data = {
        data: (responseData.data.data || []).map(transformApiProductToPublicProduct),
        pagination: responseData.data.pagination,
      };
    } else if (responseData.content && Array.isArray(responseData.content)) {
      // Format: Spring Boot Page { content: [], pageable: {}, totalElements, totalPages, ... }
      data = {
        data: responseData.content.map(transformApiProductToPublicProduct),
        pagination: {
          page: responseData.pageable?.pageNumber ?? responseData.number ?? params.page,
          pageSize: responseData.pageable?.pageSize ?? responseData.size ?? params.size,
          totalItems: responseData.totalElements ?? responseData.content.length,
          totalPages: responseData.totalPages ?? 1,
          hasNext: !responseData.last,
          hasPrev: !responseData.first,
        },
      };
    } else if (responseData.data && responseData.pagination) {
      // Format: { data: [], pagination: {} }
      data = {
        data: (responseData.data || []).map(transformApiProductToPublicProduct),
        pagination: responseData.pagination,
      };
    } else if (Array.isArray(responseData)) {
      // Format: [ products... ] (fallback)
      data = {
        data: responseData.map(transformApiProductToPublicProduct),
        pagination: {
          page: params.page,
          pageSize: params.size,
          totalItems: responseData.length,
          totalPages: 1,
          hasNext: false,
          hasPrev: false,
        },
      };
    } else {
      throw new MarketplacePublicApiError(
        'Invalid response format from API',
        500,
        'INVALID_RESPONSE'
      );
    }

    console.log('[MarketplacePublicService] Successfully fetched featured products:', {
      count: data.data.length,
      totalPages: data.pagination.totalPages,
      totalItems: data.pagination.totalItems,
    });

    return data;
  } catch (error) {
    console.error('[MarketplacePublicService] Error fetching featured products:', error);

    if (error instanceof MarketplacePublicApiError) {
      throw error;
    }

    if (error instanceof Error) {
      // Handle network errors
      if (error.message.includes('Failed to fetch')) {
        throw new MarketplacePublicApiError(
          'Network error. Please check your internet connection.',
          0,
          'NETWORK_ERROR'
        );
      }

      throw new MarketplacePublicApiError(error.message, 500, 'FETCH_ERROR');
    }

    throw new MarketplacePublicApiError(
      'Unknown error fetching featured products',
      500,
      'UNKNOWN_ERROR'
    );
  }
}

/**
 * Fetch single product by ID (public)
 * GET /api/v1/public/marketplace/products/{id}
 *
 * @param productId - Product ID to fetch
 * @returns Product details
 * @throws MarketplacePublicApiError on API failure
 *
 * @example
 * const product = await fetchPublicProductById('prod-123');
 */
export async function fetchPublicProductById(productId: string): Promise<PublicProduct> {
  try {
    console.log('[MarketplacePublicService] Fetching product by ID:', productId);

    const url = `${COMMERCE_SERVICE_URL}${API_BASE}/products/${productId}`;

    const response = await fetch(url, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new MarketplacePublicApiError(
        errorData.message || `HTTP error! status: ${response.status}`,
        response.status,
        errorData.errorCode
      );
    }

    const responseData = await response.json();

    let product: PublicProduct;

    if (responseData.success) {
      product = transformApiProductToPublicProduct(responseData.data);
    } else if (responseData.id || responseData.productName) {
      product = transformApiProductToPublicProduct(responseData);
    } else {
      throw new MarketplacePublicApiError(
        'Invalid response format from API',
        500,
        'INVALID_RESPONSE'
      );
    }

    console.log('[MarketplacePublicService] Successfully fetched product:', productId);

    return product;
  } catch (error) {
    console.error('[MarketplacePublicService] Error fetching product by ID:', error);

    if (error instanceof MarketplacePublicApiError) {
      throw error;
    }

    if (error instanceof Error) {
      if (error.message.includes('404')) {
        throw new MarketplacePublicApiError('Product not found', 404, 'PRODUCT_NOT_FOUND');
      }
      if (error.message.includes('Failed to fetch')) {
        throw new MarketplacePublicApiError(
          'Network error. Please check your internet connection.',
          0,
          'NETWORK_ERROR'
        );
      }
      throw new MarketplacePublicApiError(error.message, 500, 'FETCH_ERROR');
    }

    throw new MarketplacePublicApiError(
      'Unknown error fetching product',
      500,
      'UNKNOWN_ERROR'
    );
  }
}

/**
 * Search products by query (public)
 * GET /api/v1/public/marketplace/products/search
 *
 * @param query - Search query string
 * @param params - Query parameters (page, size, sort)
 * @returns Paginated products response
 * @throws MarketplacePublicApiError on API failure
 *
 * @example
 * const results = await searchPublicProducts('biogas', {
 *   page: 0,
 *   size: 20,
 *   sort: 'createdAt,desc'
 * });
 */
export async function searchPublicProducts(
  query: string,
  params: FeaturedProductsParams
): Promise<PaginatedPublicProductsResponse> {
  try {
    console.log('[MarketplacePublicService] Searching products:', { query, params });

    const queryParams = new URLSearchParams({
      q: query,
      page: params.page.toString(),
      size: params.size.toString(),
      sort: params.sort,
    });

    const url = `${COMMERCE_SERVICE_URL}${API_BASE}/products/search?${queryParams.toString()}`;

    const response = await fetch(url, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new MarketplacePublicApiError(
        errorData.message || `HTTP error! status: ${response.status}`,
        response.status,
        errorData.errorCode
      );
    }

    const responseData = await response.json();

    let data: PaginatedPublicProductsResponse;

    if (responseData.success) {
      data = {
        data: (responseData.data.data || []).map(transformApiProductToPublicProduct),
        pagination: responseData.data.pagination,
      };
    } else if (responseData.content && Array.isArray(responseData.content)) {
      data = {
        data: responseData.content.map(transformApiProductToPublicProduct),
        pagination: {
          page: responseData.pageable?.pageNumber ?? responseData.number ?? params.page,
          pageSize: responseData.pageable?.pageSize ?? responseData.size ?? params.size,
          totalItems: responseData.totalElements ?? responseData.content.length,
          totalPages: responseData.totalPages ?? 1,
          hasNext: !responseData.last,
          hasPrev: !responseData.first,
        },
      };
    } else if (responseData.data && responseData.pagination) {
      data = {
        data: (responseData.data || []).map(transformApiProductToPublicProduct),
        pagination: responseData.pagination,
      };
    } else {
      throw new MarketplacePublicApiError(
        'Invalid response format from API',
        500,
        'INVALID_RESPONSE'
      );
    }

    console.log('[MarketplacePublicService] Search results:', {
      count: data.data.length,
      totalItems: data.pagination.totalItems,
    });

    return data;
  } catch (error) {
    console.error('[MarketplacePublicService] Error searching products:', error);

    if (error instanceof MarketplacePublicApiError) {
      throw error;
    }

    if (error instanceof Error) {
      if (error.message.includes('Failed to fetch')) {
        throw new MarketplacePublicApiError(
          'Network error. Please check your internet connection.',
          0,
          'NETWORK_ERROR'
        );
      }
      throw new MarketplacePublicApiError(error.message, 500, 'SEARCH_ERROR');
    }

    throw new MarketplacePublicApiError(
      'Unknown error searching products',
      500,
      'UNKNOWN_ERROR'
    );
  }
}

/**
 * Fetch products with advanced filtering (public)
 * GET /api/v1/public/marketplace/products
 *
 * @param filters - Filter options including category, location, search, sort
 * @returns Paginated products response
 * @throws MarketplacePublicApiError on API failure
 *
 * @example
 * const results = await fetchProductsWithFilters({
 *   search: 'biogas',
 *   category: 'BIOGAS',
 *   state: 'Karnataka',
 *   district: 'Bangalore',
 *   sortBy: 'newest',
 *   page: 0,
 *   pageSize: 20
 * });
 */
export async function fetchProductsWithFilters(filters: {
  search?: string;
  category?: string | null;
  state?: string | null;
  district?: string | null;
  sortBy?: 'newest' | 'price_low' | 'price_high';
  page: number;
  pageSize: number;
}): Promise<PaginatedPublicProductsResponse> {
  try {
    console.log('[MarketplacePublicService] Fetching products with filters:', filters);

    // Build query parameters dynamically
    const queryParams = new URLSearchParams({
      page: filters.page.toString(),
      size: filters.pageSize.toString(),
    });

    // Add optional filters
    if (filters.search) {
      queryParams.append('q', filters.search);
    }
    if (filters.category) {
      queryParams.append('category', filters.category);
    }
    if (filters.state) {
      queryParams.append('state', filters.state);
    }
    if (filters.district) {
      queryParams.append('district', filters.district);
    }

    // Map sort option to API sort parameter
    let sortParam = 'createdAt,desc'; // default: newest
    if (filters.sortBy === 'price_low') {
      sortParam = 'price,asc';
    } else if (filters.sortBy === 'price_high') {
      sortParam = 'price,desc';
    }
    queryParams.append('sort', sortParam);

    const url = `${COMMERCE_SERVICE_URL}${API_BASE}/products?${queryParams.toString()}`;

    const response = await fetch(url, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new MarketplacePublicApiError(
        errorData.message || `HTTP error! status: ${response.status}`,
        response.status,
        errorData.errorCode
      );
    }

    const responseData = await response.json();

    let data: PaginatedPublicProductsResponse;

    if (responseData.success) {
      data = {
        data: (responseData.data.data || []).map(transformApiProductToPublicProduct),
        pagination: responseData.data.pagination,
      };
    } else if (responseData.content && Array.isArray(responseData.content)) {
      // Spring Boot Page format
      data = {
        data: responseData.content.map(transformApiProductToPublicProduct),
        pagination: {
          page: responseData.pageable?.pageNumber ?? responseData.number ?? filters.page,
          pageSize: responseData.pageable?.pageSize ?? responseData.size ?? filters.pageSize,
          totalItems: responseData.totalElements ?? responseData.content.length,
          totalPages: responseData.totalPages ?? 1,
          hasNext: !responseData.last,
          hasPrev: !responseData.first,
        },
      };
    } else if (responseData.data && responseData.pagination) {
      data = {
        data: (responseData.data || []).map(transformApiProductToPublicProduct),
        pagination: responseData.pagination,
      };
    } else if (Array.isArray(responseData)) {
      data = {
        data: responseData.map(transformApiProductToPublicProduct),
        pagination: {
          page: filters.page,
          pageSize: filters.pageSize,
          totalItems: responseData.length,
          totalPages: 1,
          hasNext: false,
          hasPrev: false,
        },
      };
    } else {
      throw new MarketplacePublicApiError(
        'Invalid response format from API',
        500,
        'INVALID_RESPONSE'
      );
    }

    console.log('[MarketplacePublicService] Filter results:', {
      count: data.data.length,
      totalItems: data.pagination.totalItems,
      filters,
    });

    return data;
  } catch (error) {
    console.error('[MarketplacePublicService] Error fetching filtered products:', error);

    if (error instanceof MarketplacePublicApiError) {
      throw error;
    }

    if (error instanceof Error) {
      if (error.message.includes('Failed to fetch')) {
        throw new MarketplacePublicApiError(
          'Network error. Please check your internet connection.',
          0,
          'NETWORK_ERROR'
        );
      }
      throw new MarketplacePublicApiError(error.message, 500, 'FILTER_ERROR');
    }

    throw new MarketplacePublicApiError(
      'Unknown error fetching filtered products',
      500,
      'UNKNOWN_ERROR'
    );
  }
}

/**
 * Inquiry Submission Data Interface
 * Data structure for submitting product inquiries
 * Maps to backend CreatePublicInquiryRequest DTO
 */
export interface InquirySubmissionData {
  productId: string;
  name: string;
  email: string;
  phone: string;
  quantity: number;
  message: string;
}

/**
 * B2B Inquiry Submission Data Interface
 * Extended data structure for B2B marketplace inquiries
 * Includes quantity, location which are formatted into the message
 */
export interface B2BInquirySubmissionData {
  productExternalId: string;
  productName: string;
  customerName: string;
  customerEmail: string;
  customerPhone: string;
  quantity: number;
  quantityUnit: string;
  location: string;
  additionalRequirements?: string;
}

/**
 * Inquiry Submission Response Interface
 * Response structure from inquiry submission API
 */
export interface InquirySubmissionResponse {
  success: boolean;
  message: string;
  inquiryId?: string;
}

/**
 * Submit product inquiry (public)
 * POST /api/v1/public/marketplace/inquiries
 *
 * @param data - Inquiry submission data
 * @returns Inquiry submission response
 * @throws MarketplacePublicApiError on API failure
 *
 * @example
 * const response = await submitProductInquiry({
 *   productId: 'prod-123',
 *   name: 'John Doe',
 *   email: 'john@example.com',
 *   phone: '+919876543210',
 *   quantity: 5,
 *   message: 'I am interested in purchasing this product...'
 * });
 */
export async function submitProductInquiry(
  data: InquirySubmissionData
): Promise<InquirySubmissionResponse> {
  try {
    console.log('[MarketplacePublicService] Submitting product inquiry:', {
      productId: data.productId,
      name: data.name,
      email: data.email,
    });

    const url = `${COMMERCE_SERVICE_URL}${API_BASE}/inquiries`;

    // Transform frontend field names to match backend API expectations
    // Backend expects: productId, customerName, customerPhone, customerEmail, message, quantity
    const requestPayload = {
      productId: parseInt(data.productId, 10),
      customerName: data.name,
      customerPhone: data.phone,
      customerEmail: data.email,
      message: data.message,
      quantity: data.quantity,
    };

    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(requestPayload),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new MarketplacePublicApiError(
        errorData.message || `HTTP error! status: ${response.status}`,
        response.status,
        errorData.errorCode
      );
    }

    const responseData = await response.json();

    let result: InquirySubmissionResponse;

    if (responseData.success !== undefined) {
      result = {
        success: responseData.success,
        message: responseData.message || 'Inquiry submitted successfully',
        inquiryId: responseData.data?.id || responseData.inquiryId,
      };
    } else {
      // Assume success if no explicit success field
      result = {
        success: true,
        message: 'Inquiry submitted successfully',
        inquiryId: responseData.id || responseData.inquiryId,
      };
    }

    console.log('[MarketplacePublicService] Inquiry submitted successfully:', {
      inquiryId: result.inquiryId,
    });

    return result;
  } catch (error) {
    console.error('[MarketplacePublicService] Error submitting inquiry:', error);

    if (error instanceof MarketplacePublicApiError) {
      throw error;
    }

    if (error instanceof Error) {
      if (error.message.includes('Failed to fetch')) {
        throw new MarketplacePublicApiError(
          'Network error. Please check your internet connection.',
          0,
          'NETWORK_ERROR'
        );
      }
      throw new MarketplacePublicApiError(error.message, 500, 'INQUIRY_SUBMISSION_ERROR');
    }

    throw new MarketplacePublicApiError(
      'Unknown error submitting inquiry',
      500,
      'UNKNOWN_ERROR'
    );
  }
}

/**
 * Format phone number to Indian format (+91XXXXXXXXXX)
 * @param phone - Phone number in any format
 * @returns Formatted phone number
 */
function formatPhoneToIndian(phone: string): string {
  // Remove all non-digit characters except +
  const cleaned = phone.replace(/[^\d+]/g, '');

  // If already has +91, return as is
  if (cleaned.startsWith('+91') && cleaned.length === 13) {
    return cleaned;
  }

  // Extract last 10 digits
  const digits = cleaned.replace(/\D/g, '');
  const last10 = digits.slice(-10);

  // Ensure it starts with valid Indian mobile prefix (6-9)
  if (last10.length === 10 && /^[6-9]/.test(last10)) {
    return `+91${last10}`;
  }

  // Return original if can't format
  return phone;
}

/**
 * Submit B2B product inquiry (public)
 * POST /api/v1/public/marketplace/inquiries
 *
 * Sends quantity and location as dedicated fields to the backend.
 * The message field contains additional requirements or a default quote request message.
 *
 * @param data - B2B Inquiry submission data
 * @returns Inquiry submission response
 * @throws MarketplacePublicApiError on API failure
 *
 * @example
 * const response = await submitB2BInquiry({
 *   productExternalId: '550e8400-e29b-41d4-a716-446655440000',
 *   productName: 'Organic Fertilizer',
 *   customerName: 'John Doe',
 *   customerEmail: 'john@example.com',
 *   customerPhone: '9876543210',
 *   quantity: 500,
 *   quantityUnit: 'kg',
 *   location: 'Delhi',
 *   additionalRequirements: 'Need delivery by next week'
 * });
 */
export async function submitB2BInquiry(
  data: B2BInquirySubmissionData
): Promise<InquirySubmissionResponse> {
  try {
    console.log('[MarketplacePublicService] Submitting B2B inquiry:', {
      productExternalId: data.productExternalId,
      productName: data.productName,
      customerName: data.customerName,
      quantity: data.quantity,
      quantityUnit: data.quantityUnit,
      location: data.location,
    });

    const url = `${COMMERCE_SERVICE_URL}${API_BASE}/inquiries`;

    // Format phone number to Indian format
    const formattedPhone = formatPhoneToIndian(data.customerPhone);

    // Build message - use additional requirements or default quote request
    const message = data.additionalRequirements && data.additionalRequirements.trim()
      ? data.additionalRequirements.trim()
      : `Quote request for ${data.productName}`;

    // Build request payload matching backend CreatePublicInquiryRequest
    // Now with dedicated quantity, quantityUnit, and location fields
    const requestPayload = {
      productId: parseInt(data.productExternalId, 10),
      customerName: data.customerName,
      customerPhone: formattedPhone,
      customerEmail: data.customerEmail || undefined,
      message: message,
      quantity: data.quantity,
      quantityUnit: data.quantityUnit,
      location: data.location,
    };

    console.log('[MarketplacePublicService] Request payload:', requestPayload);

    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(requestPayload),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new MarketplacePublicApiError(
        errorData.message || `HTTP error! status: ${response.status}`,
        response.status,
        errorData.errorCode
      );
    }

    const responseData = await response.json();

    const result: InquirySubmissionResponse = {
      success: true,
      message: responseData.message || 'Inquiry submitted successfully',
      inquiryId: responseData.inquiryId?.toString() || responseData.id?.toString(),
    };

    console.log('[MarketplacePublicService] B2B Inquiry submitted successfully:', {
      inquiryId: result.inquiryId,
    });

    return result;
  } catch (error) {
    console.error('[MarketplacePublicService] Error submitting B2B inquiry:', error);

    if (error instanceof MarketplacePublicApiError) {
      throw error;
    }

    if (error instanceof Error) {
      if (error.message.includes('Failed to fetch')) {
        throw new MarketplacePublicApiError(
          'Network error. Please check your internet connection.',
          0,
          'NETWORK_ERROR'
        );
      }
      throw new MarketplacePublicApiError(error.message, 500, 'INQUIRY_SUBMISSION_ERROR');
    }

    throw new MarketplacePublicApiError(
      'Unknown error submitting inquiry',
      500,
      'UNKNOWN_ERROR'
    );
  }
}
